/**
 * This script handles lazy loading of weather radar widgets using IntersectionObserver.
 * It is designed to work both on initial page load (DOMContentLoaded) and
 * for dynamically loaded content in Unpoly.
 */
(function(w, d) {
    const WEATHER_RADAR_INITIALIZED_FLAG = 'data-weather-radar-initialized';
    const unpolyRadar = false; //toggle for unpoly
    const weatherRadarLazyLoader = {
        // We'll manage observers within a WeakMap to link them to their respective radar containers
        // This is important because Unpoly might re-process elements, and we don't want
        // to create multiple observers for the same element, nor leave old observers active.
        _observers: new WeakMap(),

        /**
         * Initializes a single .weather-radar container for lazy loading.
         * This function will be called by both DOMContentLoaded and Unpoly compiler.
         * @param {HTMLElement} radarContainer The .weather-radar element to observe.
         */
        initializeRadar: function(radarContainer) {
            // Prevent re-initialization if this radar container has already been processed
            if (radarContainer.hasAttribute(WEATHER_RADAR_INITIALIZED_FLAG)) {
                return;
            }

            // Mark as initialized to prevent future re-initializations by this script
            radarContainer.setAttribute(WEATHER_RADAR_INITIALIZED_FLAG, 'true');

            // If template/IntersectionObserver not supported, load immediately for this container
            if (!('content' in d.createElement('template')) || !('IntersectionObserver' in w)) {
                this._loadRadarContent(radarContainer);
                return;
            }

            // If it's already marked as loaded, just unobserve if an old observer exists and return
            if (radarContainer.dataset.loaded === "true") {
                 // Clean up any old observer if present (though Unpoly usually cleans up old elements)
                if (this._observers.has(radarContainer)) {
                    this._observers.get(radarContainer).unobserve(radarContainer);
                    this._observers.delete(radarContainer);
                }
                return;
            }


            const intersectionCallback = function(entries, observer) {
                entries.forEach(function(entry) {
                    if (entry.isIntersecting) {
                        let currentRadarContainer = entry.target; // This is the .weather-radar div
                        weatherRadarLazyLoader._loadRadarContent(currentRadarContainer);
                        observer.unobserve(currentRadarContainer); // Stop observing once loaded
                        weatherRadarLazyLoader._observers.delete(currentRadarContainer); // Clean up from map
                    }
                });
            };

            const observerConfig = {
                rootMargin: "0px 0px 200px 0px", // Load when 200px from viewport bottom
                threshold: 0.01 // Trigger as soon as 1% is visible
            };
            let intersectionObserver = new w.IntersectionObserver(intersectionCallback, observerConfig);

            intersectionObserver.observe(radarContainer);
            this._observers.set(radarContainer, intersectionObserver); // Store the observer
        },

        /**
         * Loads the content for a single weather radar container.
         * @param {HTMLElement} radarContainer The .weather-radar element.
         */
        _loadRadarContent: function(radarContainer) {
            if (radarContainer.dataset.loaded === "true") {
                // console.log(`Radar for ${radarContainer.id || radarContainer.className} already processed/loaded.`);
                return; // Already initiated loading, prevent duplicate
            }
            radarContainer.dataset.loaded = "true"; // Mark as loading initiated

            const spinner = radarContainer.querySelector(".loading-spinner");
            const templateId = radarContainer.dataset.template;
            let template = d.getElementById(templateId);

            if (!template) {
                if (spinner) spinner.innerHTML = "<div class='spinner-text'>Error: Template missing.</div>";
                return;
            }

            const widgetFragment = template.content.cloneNode(true);
            radarContainer.appendChild(widgetFragment);

            if (w.wxWidgets && typeof w.wxWidgets.parse === 'function') {
                w.wxWidgets.parse(radarContainer); // Pass the .weather-radar container to parse
            } else {
                console.warn("wxWidgets.parse function not found.");
                if (spinner && spinner.parentNode) radarContainer.removeChild(spinner);
            }

            // Observe the radarContainer for the addition of the actual map content
            if (spinner) {
                let mutationObserver = new MutationObserver(function(mutations, obs) {
                    const mapWidgetDiv = radarContainer.querySelector('div[id^="wxmap-widget-"]');
                    const mapCanvas = radarContainer.querySelector('canvas.mapboxgl-canvas');

                    if (mapWidgetDiv && mapCanvas && mapCanvas.width > 0 && mapCanvas.height > 0) {
                        if (spinner.parentNode === radarContainer) {
                            radarContainer.removeChild(spinner);
                        }
                        obs.disconnect(); // Stop observing mutations for this radarContainer
                    }
                });

                mutationObserver.observe(radarContainer, {
                    childList: true,
                    subtree: true
                });

                // Safety timeout to remove spinner if mutation observer condition is never met
                setTimeout(() => {
                    if (spinner.parentNode === radarContainer) {
                        radarContainer.removeChild(spinner);
                    }
                    mutationObserver.disconnect(); // Ensure observer is disconnected
                }, 10000); // 10 seconds timeout
            }
        }
    };

    // --- Main execution logic for the weather radar lazy loader ---
    if (w.up && w.up.version && unpolyRadar) {
        // Unpoly is available, use up.compiler for robust loading
        // This will find all .weather-radar elements, including those loaded via AJAX
        w.up.compiler('.weather-radar', function(radarElement) {
            weatherRadarLazyLoader.initializeRadar(radarElement);
        });

        // Add a global event listener for `up:fragment:removed` to clean up IntersectionObservers
        // attached to elements that are removed from the DOM by Unpoly.
        // This prevents memory leaks and ensures observers don't fire for non-existent elements.
        w.up.on('up:fragment:removed', function(event) {
            const removedElements = event.elements;
            removedElements.forEach(el => {
                // Check if the removed element itself was a radar container
                if (el.matches('.weather-radar')) {
                    if (weatherRadarLazyLoader._observers.has(el)) {
                        weatherRadarLazyLoader._observers.get(el).disconnect();
                        weatherRadarLazyLoader._observers.delete(el);
                        // console.log("Disconnected observer for removed radar:", el);
                    }
                }
                // Also check for descendant radar containers within the removed fragment
                el.querySelectorAll('.weather-radar').forEach(descendantRadar => {
                    if (weatherRadarLazyLoader._observers.has(descendantRadar)) {
                        weatherRadarLazyLoader._observers.get(descendantRadar).disconnect();
                        weatherRadarLazyLoader._observers.delete(descendantRadar);
                        // console.log("Disconnected observer for descendant radar:", descendantRadar);
                    }
                });
            });
        });

    } else {
        // Unpoly is not available, fall back to DOMContentLoaded for initial page load
        d.addEventListener("DOMContentLoaded", function() {
            //console.log("DOMContentLoaded fired. Initializing weather radars.");
            d.querySelectorAll(".weather-radar").forEach(function(radarEl) {
                weatherRadarLazyLoader.initializeRadar(radarEl);
            });
        });
    }

})(window, document);